/**
 * Phase 140B.2 — Highlight Marker Button (broadcaster cockpit).
 *
 * Single ⭐ button + a tiny popover that lets the operator tag the current
 * moment as a highlight of a specific type (goal / great_play / save /
 * celebration / wicket / crowd). Posts to the existing
 * `POST /api/livex/streams/{streamId}/highlights` endpoint which already
 * persists the highlight with sport-aware match-time anchoring and clip
 * intent offsets (Sprint 3 clip-extraction worker will pick these up).
 *
 * Behaviour:
 *   - Idle state: a big ⭐ "Mark Highlight" button.
 *   - On click → opens a 6-tile picker (Goal / Great Play / Wicket /
 *     Celebration / Crowd / Other).
 *   - On pick → POSTs with `trigger_type='manual'`, `confidence=1.0`,
 *     auto-dismisses, and fires a toast with the match-time label so the
 *     operator can verify the moment they captured.
 *   - Errors → toast with the API error message, picker stays open so the
 *     operator can retry.
 */
import { useState } from 'react';
import { Star, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const TYPES = [
  { id: 'goal',        emoji: '⚽', label: 'Goal' },
  { id: 'great_play',  emoji: '⭐', label: 'Great Play' },
  { id: 'wicket',      emoji: '🏏', label: 'Wicket' },
  { id: 'celebration', emoji: '👏', label: 'Celebration' },
  { id: 'crowd',       emoji: '🔥', label: 'Crowd Moment' },
  { id: 'other',       emoji: '✨', label: 'Other' },
];

export default function HighlightMarkerButton({ streamId, sport }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  // Sport-aware default: cricket promotes "wicket" to the first tile.
  const ordered = sport === 'cricket'
    ? [TYPES[2], ...TYPES.filter((t) => t.id !== 'wicket')]
    : TYPES;

  async function mark(type) {
    if (busy) return;
    setBusy(type.id);
    try {
      const res = await api.post(`/livex/streams/${streamId}/highlights`, {
        type: type.id,
        note: '',
        trigger_type: 'manual',
        confidence: 1.0,
      });
      const label = res?.data?.match_time_label || '';
      toast.success(`${type.emoji} ${type.label} marked${label ? ` at ${label}` : ''}`);
      setOpen(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not mark highlight. Retry?';
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="relative" data-testid="highlight-marker">
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="highlight-marker-open"
          className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-bold shadow-lg transition-all"
        >
          <Star className="h-4 w-4 fill-white" />
          <span>Mark Highlight</span>
        </button>
      )}

      {open && (
        <div
          className="p-4 bg-[#07111F] border-2 border-amber-500/60 rounded-xl space-y-3 shadow-2xl"
          data-testid="highlight-marker-picker"
        >
          <div className="flex items-center justify-between">
            <div className="text-sm font-bold text-amber-300">Tag this moment</div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="highlight-marker-close"
              className="text-xs text-white/60 hover:text-white"
            >
              Cancel
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {ordered.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => mark(t)}
                disabled={!!busy}
                data-testid={`highlight-marker-type-${t.id}`}
                className="flex flex-col items-center justify-center gap-1 p-3 rounded-lg bg-[#0c1a2e] border-2 border-[#22314F] hover:border-amber-500 transition-all disabled:opacity-50"
              >
                <span className="text-2xl" aria-hidden>{t.emoji}</span>
                <span className="text-[11px] text-white font-bold leading-tight text-center">
                  {busy === t.id ? <Loader2 className="h-3 w-3 animate-spin inline" /> : t.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
